import { PART_HELP } from "../help/pages";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AnnotationData, RaceChartData } from "@shared/types";
import { carKey, carNo } from "./car-number";
import { TRACE, labelOn, liveClassColor } from "./constants";
import { CHIP_H, layoutPitChips, outlinePoints, type PitChipsLayout } from "./pit-chips";
import { mapInsets, type Rect } from "./map-room";
import { readFollowedCars, saveFollowedCars, toggleFollow } from "../live/my-car";
import { RunningOrder, type PitVisit } from "./RunningOrder";
import { lapsDown } from "./laps-down";
import { PhoneMapBar, PitList } from "./PitList";
import { useIsCompact } from "../../lib/breakpoint";
import { mapFlag } from "./flags";
import { CALL_EARLY_MAX_S, FALLBACK_LOSS_S, GHOST_MAX_S, ghostKey, ghostLoss, ghostPlace, MAX_GHOSTS, readPitGhost, savePitGhost, WINDOW_MAX_S, type GhostShown, type PitGhost } from "./pit-ghost";
import { greenStopForCar, liveStrategy, pitLaneState, pitNowAll, WRL_CAUTION_RATES, type CautionRates, type RaceHistory } from "./live-strategy";
import { FOLLOWED_STEP_S, ghostLabel, ghostTraffic, trafficAt } from "./ghost-traffic";
import { boxCall, type BoxCall, type BoxTone } from "./box-call";
import { InfoTip } from "../../components/InfoTip";
import { readMapAngle, rotatedFit, rotatedSize, saveMapAngle, snapDeg, stepDeg } from "./map-rotate";
import { virtualCars } from "../live/race-laps";
import { RaceControl, type RaceControlMessage } from "./RaceControl";
import { LiveTrackOrder, leaderOrder, leaderRef, trackOrder, type TrackPlace } from "./LiveTrackOrder";
import { closingOnFocus, SOON_S, URGENT_S, type Closing } from "./closing";
import { driversInCars } from "./DriverReportTab";
import {
  EST,
  IN_PIT,
  NO_GPS,
  ahead,
  OVERDUE,
  STALE,
  isFrozen,
  isStopped,
  pointAt,
  secondsToPitEntry,
  visibility,
  type CarMotion,
} from "./track-motion";
import { carPlaces, isCaution, pitVisitsNow } from "./live-field";
import { useLiveField, type LiveField } from "./useLiveField";

export { isCaution, pitVisitsNow };

/** Older than this, the map says how old its positions are. */
const OLD_MS = 20_000;

const C = {
  bg: "#0b0b18",
  track: "#23233f",
  trackIn: "#2e2e54",
  line: "#e0e0e8",
  muted: "#9a9ab4",
  text: "#e0e0e8",
  /** The overall leader's ring and tag. */
  leader: "#fbbf24",
  /** The dashed ring round a car off the track. */
  offTrack: "#f97316",
  /** The ring round a stopped car. */
  stopped: "#ef4444",
  /** The ring round a car on its out-lap from the pits. */
  outLap: "#22d3ee",
  /** The pit entry tag (the running order's blue P). */
  pit: "#2563eb",
  /** The pit road, learned from the cars' paths in and out. */
  pitRoad: "rgba(37,99,235,0.35)",
};

/** A car on the pit road this near the PIT tag (px) is listed beside it with the cars in the pit lane, not drawn on its own. */
const PIT_NEAR_TAG_PX = 36;
/** How often the panels floating over the map are measured (ms). */
const OVERLAY_MS = 400;
/** At most this many chips beside the PIT tag (then "+n"), each kept this far (px) from the track's centreline: its half width (8) and a gap. */
const PIT_CHIPS_MAX = 5;
const PIT_CLEAR_PX = 11;


/**
 * RaceTrace Live: the circuit with every car as a numbered dot moving round it ("marching ants").
 * Every dot is in its class colour (GTU grey, GTO blue, GP1 red, GP2 green). The focus car is the
 * biggest, with a white ring; compare cars are larger, numbered; the rest small; with one class
 * picked, the other classes are faint. Beside it, the running order (position, number,
 * place in class, by class colour); cars in the pit lane are listed under the map. Tapping a dot or
 * a row follows that car (or stops following it); the focus car is set with Focus. Followed cars
 * are drawn like the focus car, large with a glow and a ring, so several cars can be tracked at
 * once, and their rows are highlighted.
 */
export function TrackMap({
  slug,
  data,
  focusNum,
  setFocusNum,
  compSet,
  classView,
  flag = null,
  clock = null,
  annotations = null,
  trackName = "",
  field,
  cautionRates,
  history = null,
  control,
  mapNote = null,
}: {
  slug: string;
  data: RaceChartData;
  focusNum: number;
  setFocusNum: (n: number) => void;
  compSet: Set<number>;
  classView: string;
  /** The session's flag now ("Yellow", "Code 35"…): under a caution the safety car is shown. */
  flag?: string | null;
  /** The race clock: time to go (s) at a moment (client clock), frozen under a red flag. For the virtual cars. */
  clock?: { ttgS: number; at: number; frozen: boolean } | null;
  /** The race's notes (its pit stops), for the running order. */
  annotations?: AnnotationData | null;
  /** The circuit's name: the angle the viewer turns the map to is kept per circuit. */
  trackName?: string;
  /** The live page's field (useLiveField.ts), shared with its other tabs; without one the map reads its own. */
  field?: LiveField;
  /** The race's series' caution rates (cautionRatesFor); WRL's when not given. */
  cautionRates?: CautionRates;
  /** What the series' earlier races say (GET /api/live/:slug/history); none until loaded. */
  history?: RaceHistory | null;
  /** Race control's messages from elsewhere (a race replay's), instead of the session's log. */
  control?: RaceControlMessage[] | null;
  /** A note about the map itself, first among its notes (a replay's schematic circuit). */
  mapNote?: string | null;
}) {
  const compact = useIsCompact();
  // The cars on the track: the page's, else the map's own (the pre-race page).
  const ownField = useLiveField({ slug, data, flag, enabled: !field });
  const f = field ?? ownField;
  const { track, trackMissing, motions, pitLane, readingAt, pits, pitVisits, pitSpots, unplaced, offTrack, lostSignal, overdue, out, now, typicalSpeed, greenPace } = f;
  /** Laps down to the class leader (the LD column): a lapped car is drawn as a ghost dot. */
  const lapped = useMemo(() => lapsDown(data), [data]);
  /** What the map leaves out, picked with the key's chips under it (kept on this device). */
  const [hidden, setHidden] = useState<Set<string>>(readHiddenLayers);
  const toggleLayer = (k: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (!next.delete(k)) next.add(k);
      saveHiddenLayers(next);
      return next;
    });
  /** Beside the map: the running order (timing) or the Live Crossing Order (on the road now). */
  const [orderView, setOrderView] = useState<"timing" | "live">(() => {
    try {
      return localStorage.getItem(ORDER_VIEW_KEY) === "live" ? "live" : "timing";
    } catch {
      return "timing";
    }
  });
  const pickOrderView = (v: "timing" | "live") => {
    setOrderView(v);
    try {
      localStorage.setItem(ORDER_VIEW_KEY, v);
    } catch {
      /* private window: for this visit only */
    }
  };
  /** The Live Crossing Order from the focus car (the cars round it) or from the leader (on top), kept on this device. */
  const [crossFrom, setCrossFrom] = useState<"focus" | "leader">(() => {
    try {
      return localStorage.getItem(CROSS_FROM_KEY) === "leader" ? "leader" : "focus";
    } catch {
      return "focus";
    }
  });
  const pickCrossFrom = (f: "focus" | "leader") => {
    setCrossFrom(f);
    try {
      localStorage.setItem(CROSS_FROM_KEY, f);
    } catch {
      /* private window: for this visit only */
    }
  };
  const drivers = useMemo(() => (orderView === "live" ? driversInCars(data, annotations ?? {}) : new Map<string, string>()), [orderView, data, annotations]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hits = useRef<{ key: number; x: number; y: number; r: number }[]>([]);
  /** The pit ghost (pit-ghost.ts): on or off and the time a stop would lose, kept on this device. */
  const [pitGhost, setPitGhost] = useState<PitGhost>(readPitGhost);
  const changePitGhost = (g: PitGhost) => {
    setPitGhost(g);
    savePitGhost(g);
  };
  const pitGhostRef = useRef(pitGhost);
  /** The ghosts shown (worked out each second): whose, the time each loses, the traffic around it, for the rings and labels on the track. */
  const ghostsRef = useRef<GhostShown[]>([]);
  pitGhostRef.current = pitGhost;
  /**
   * Every car's "if it stops now" in its class (the Live Crossing Order's Box column). The time to
   * go moves each second; taken to the nearest 10 s, so the estimate is worked out again only when
   * the chart, the flag or that changes.
   */
  const ttgS = clock ? Math.max(0, clock.frozen ? clock.ttgS : clock.ttgS - (now - clock.at) / 1000) : null;
  const ttgStep = ttgS === null ? null : Math.round(ttgS / 10) * 10;
  /** Each car's next stop as the Strategy tab charges it (pitNowAll: one figure per car), the ghosts' time unless one is typed. */
  const ghostNow = useMemo(
    () => (pitGhost.on ? pitNowAll(data, annotations ?? null, flag ?? null, ttgStep, cautionRates, history) : null),
    [pitGhost.on, data, annotations, flag, ttgStep, cautionRates, history]
  );
  const stopFigure = (key: number) => ghostNow?.get(key)?.lostS ?? greenStopForCar(data, annotations ?? null, key);
  const measuredLossS = pitGhost.on ? stopFigure(focusNum) : null;
  const lossS = ghostLoss(pitGhost, measuredLossS);
  const boxNow = useMemo(
    () => (orderView === "live" ? pitNowAll(data, annotations ?? null, flag ?? null, ttgStep, cautionRates, history) : null),
    [orderView, data, annotations, flag, ttgStep, cautionRates, history]
  );
  /** The pit lane for the focus car's class under a caution (pitLaneState), for the pit light by the flag. */
  const focusCls = data.cars[String(focusNum)]?.cls ?? null;
  const focusPits = useMemo(
    () => (focusCls ? pitLaneState(data, focusCls, isCaution(flag), ttgStep, cautionRates ?? WRL_CAUTION_RATES) : null),
    [data, focusCls, flag, ttgStep, cautionRates]
  );
  /** The focus car's strategy in its class (its pit window), for the box call: while the pit ghost is on. */
  const focusPlan = useMemo(
    () => (pitGhost.on ? (liveStrategy({ data, annotations: annotations ?? null, focusNum, flag: flag ?? null, timeToGoS: ttgStep, cautionRates, history })?.focus ?? null) : null),
    [pitGhost.on, data, annotations, focusNum, flag, ttgStep, cautionRates, history]
  );
  /** Where a car's pit ghost is now (m past the line): `lossS` behind the car at its pace; null off the track. */
  const ghostOf = (key: number, len: number, lossS: number): number | null => {
    const m = [...motions.current.values()].find((x) => carKey(data, x.num) === key);
    if (!m || m.flags & IN_PIT || pitVisits.has(key) || out.has(key)) return null;
    return ghostPlace(m.shown, m.base > 0 ? m.base : typicalSpeed, lossS, len);
  };
  /**
   * The cars-closing feature is on or off with the key's "closing" chip (a hidden layer): off, no
   * panel and no marks. Its marks on the track have their own switch in the panel (kept on this
   * device).
   */
  const closingOn = !hidden.has("closing");
  const [closingMarks, setClosingMarks] = useState(() => {
    try {
      return localStorage.getItem(CLOSING_MARKS_KEY) !== "off";
    } catch {
      return true;
    }
  });
  const toggleClosingMarks = () =>
    setClosingMarks((on) => {
      try {
        localStorage.setItem(CLOSING_MARKS_KEY, on ? "off" : "on");
      } catch {
        /* private window: for this visit only */
      }
      return !on;
    });
  /** Who is closing on the focus car (worked out each second), for the markers drawn on the track. */
  const closingRef = useRef<Closing[]>([]);
  // The map turned to suit the viewer (map-rotate.ts): dragged with the mouse, or 45° at a time
  // with the buttons on the map. Drawn from the ref (a drag redraws without re-rendering the page);
  // the state is for the buttons' readout.
  const circuit = trackName || slug;
  const [angle, setAngle] = useState(() => readMapAngle(circuit));
  const angleRef = useRef(angle);
  useEffect(() => {
    const a = readMapAngle(circuit);
    angleRef.current = a;
    setAngle(a);
  }, [circuit]);
  const turnTo = (deg: number) => {
    angleRef.current = deg;
    setAngle(deg);
    saveMapAngle(circuit, deg);
  };
  const readout = useRef<HTMLSpanElement>(null);
  const drag = useRef<{ id: number; startA: number; base: number; x: number; y: number; moved: boolean } | null>(null);
  /** A drag ends with a click on the canvas: not a tap on a car. */
  const dragged = useRef(false);
  const fit = useRef<{ k: string; px: (p: [number, number]) => [number, number] } | null>(null);
  /** The panels floating over the map (measured every OVERLAY_MS): the room they leave the track is worked out from them (map-room.ts). */
  const overlays = useRef<{ at: number; W: number; H: number; panels: Rect[] } | null>(null);
  /** The PIT tag and chips' places (pit-chips.ts), kept until the cars in the pits or the map's fit change. */
  const pitChips = useRef<{ k: string; l: PitChipsLayout } | null>(null);
  /** The cars this viewer follows on the map (published numbers), kept per race on this device. */
  const [followed, setFollowed] = useState<string[]>(() => readFollowedCars(slug));
  useEffect(() => setFollowed(readFollowedCars(slug)), [slug]);
  const follow = (cars: string[]) => {
    setFollowed(cars);
    saveFollowedCars(slug, cars);
  };
  const followKeys = useMemo(() => new Set(followed.map((n) => carKey(data, n)).filter((k): k is number => k !== null)), [followed, data]);


  // Drawing: every frame, each car eased towards where it should be.
  const lastFrame = useRef(Date.now());
  const draw = useRef<() => void>(() => undefined);
  draw.current = () => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !track) return;
    const dpr = window.devicePixelRatio || 1;
    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    // The outline turned to the viewer's angle and fitted to the space (worked out again only when
    // the angle, the size or the outline changes).
    const pad = 28;
    // The panels floating over the map (measured every OVERLAY_MS): the track is fitted beside
    // them (map-room.ts), and the pit chips keep off them.
    const now0 = Date.now();
    if (!overlays.current || now0 - overlays.current.at > OVERLAY_MS || overlays.current.W !== W || overlays.current.H !== H) {
      const wr = wrap.getBoundingClientRect();
      const panels: Rect[] = [...(wrap.parentElement ?? wrap).querySelectorAll<HTMLElement>("[data-map-overlay]")]
        .map((el) => el.getBoundingClientRect())
        .map((r) => ({ x: r.left - wr.left, y: r.top - wr.top, w: r.width, h: r.height }))
        .filter((r) => r.w > 0 && r.h > 0 && r.x < W && r.y < H && r.x + r.w > 0 && r.y + r.h > 0);
      overlays.current = { at: now0, W, H, panels };
    }
    // The edges the panels take: the combination that draws the track largest at this angle.
    const size = rotatedSize(track.pts, track.w, track.h, angleRef.current);
    const room = mapInsets(overlays.current.panels, W, H, { ...size, pad });
    const k = `${angleRef.current}|${W}|${H}|${track.pts.length}|${track.w}|${track.h}|${Math.round(room.l)},${Math.round(room.t)},${Math.round(room.r)},${Math.round(room.b)}`;
    if (fit.current?.k !== k) fit.current = { k, px: rotatedFit(track.pts, track.w, track.h, angleRef.current, W, H, pad, room) };
    const px = fit.current.px;

    // The circuit.
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    track.pts.forEach((p, i) => {
      const [x, y] = px(p);
      if (i) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
    });
    ctx.closePath();
    // Under a caution or a red flag, the track's edge in the flag's colour (flags.ts mapFlag).
    const fl = mapFlag(flag);
    ctx.strokeStyle = fl?.tint ? fl.color : C.track;
    ctx.globalAlpha = fl?.tint ? 0.8 : 1;
    ctx.lineWidth = 16;
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = C.trackIn;
    ctx.lineWidth = 10;
    ctx.stroke();

    // The start/finish line, across the track.
    const [sx, sy] = px(pointAt(track.pts, track.ds, track.len, 0));
    const [tx, ty] = px(pointAt(track.pts, track.ds, track.len, 20));
    const ang = Math.atan2(ty - sy, tx - sx) + Math.PI / 2;
    ctx.strokeStyle = C.line;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(sx - Math.cos(ang) * 11, sy - Math.sin(ang) * 11);
    ctx.lineTo(sx + Math.cos(ang) * 11, sy + Math.sin(ang) * 11);
    ctx.stroke();

    // The pit entry: a PIT tag beside the track where the pit flag comes on, with the cars in the
    // pits in a row of chips beside it (drawn with the cars, below); pitting cars fade into it.
    let pitTag: { x: number; y: number; ux: number; uy: number } | null = null;
    if (pitLane.current) {
      const [ex, ey] = px(pointAt(track.pts, track.ds, track.len, pitLane.current.in));
      const [fx, fy] = px(pointAt(track.pts, track.ds, track.len, pitLane.current.in + 20));
      const a = Math.atan2(fy - ey, fx - ex) + Math.PI / 2;
      // The pit-in point on the track and the normal out from it: the tag goes beside it (pit-chips.ts).
      pitTag = { x: ex, y: ey, ux: Math.cos(a), uy: Math.sin(a) };
    }

    // The cars: the rest, then compare cars, the overall leader, then the focus car on top.
    const t = Date.now();
    f.stepTo(t);
    lastFrame.current = t;
    const inClass = classView ? new Set(data.classGroups[classView] ?? []) : null;
    const leaderKey = overallLeader(data);
    const dots = [...motions.current.values()]
      .map((m) => {
        const key = carKey(data, m.num);
        // Not running (OUT): listed under the map, not drawn on the track.
        if (key === null || !data.cars[String(key)] || out.has(key)) return null;
        const focus = key === focusNum;
        const compared = !focus && compSet.has(key);
        const leader = key === leaderKey;
        const followed = !focus && followKeys.has(key);
        const other = inClass !== null && !inClass.has(key) && !focus && !followed && !leader;
        const ghost = (lapped.get(key) ?? 0) > 0;
        // Left out with the key's chips (the focus car and followed cars are always drawn).
        if (!focus && !followed && (hidden.has(`cls:${data.cars[String(key)].cls}`) || (ghost && hidden.has("lapped")))) return null;
        return { m, key, focus, followed, compared, leader, other, ghost, outLap: onOutLap(data, key, pitVisits), rank: focus ? 4 : followed ? 3 : leader ? 2 : compared ? 1 : 0 };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .sort((a, b) => a.rank - b.rank || (b.other ? 1 : 0) - (a.other ? 1 : 0));
    const many = compSet.size > 8;
    hits.current = [];
    let leaderAt: { x: number; y: number; r: number } | null = null;

    // The pit road as learned (faint dots), and the cars in the pits. A car still moving on the
    // pit road away from the PIT tag (on its way in or out) is a numbered dashed marker where it is;
    // the rest (in the pit lane) are a row of numbered chips beside the PIT tag, the focus car first,
    // so a busy pit lane is a list, not a pile of rings.
    const road = pitLane.current?.road;
    if (road?.length && !hidden.has("pitLane")) {
      ctx.fillStyle = C.pitRoad;
      for (const p of road) {
        const [x, y] = px(p);
        ctx.beginPath();
        ctx.arc(x, y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    const lane: { key: number; focus: boolean; followed: boolean }[] = [];
    for (const sp of pitSpots) {
      const key = carKey(data, sp.num);
      if (key === null || !data.cars[String(key)] || out.has(key)) continue;
      const focus = key === focusNum;
      const followed = !focus && followKeys.has(key);
      if (!focus && !followed && (hidden.has("pitLane") || hidden.has(`cls:${data.cars[String(key)].cls}`))) continue;
      const at = sp.road && sp.xy ? px(sp.xy) : null;
      if (!at || !pitTag || Math.hypot(at[0] - pitTag.x, at[1] - pitTag.y) < PIT_NEAR_TAG_PX) {
        lane.push({ key, focus, followed });
        continue;
      }
      // On the pit road, on its way in or out.
      const [x, y] = at;
      const label = carNo(data, key);
      const r = focus || followed ? 10 : 8;
      ctx.fillStyle = C.bg;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.setLineDash([3, 2.5]);
      ctx.strokeStyle = focus ? TRACE.focus : liveClassColor(data.cars[String(key)]?.cls);
      ctx.lineWidth = focus || followed ? 2.5 : 1.5;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = C.text;
      ctx.font = `700 ${label.length > 3 ? 6.5 : label.length > 2 ? 7.5 : 8.5}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, x, y + 0.5);
      hits.current.push({ key, x, y, r: Math.max(r, 10) });
    }
    if (pitTag) {
      // The PIT tag beside the track, then the cars in the pits as a short line of chips from it,
      // never touching the track (pit-chips.ts): beside the tag, else in open space joined to it,
      // at most PIT_CHIPS_MAX, the rest "+n" (the PIT list beside the map names them all). With no
      // room for any, the tag counts them.
      const rank = (c: (typeof lane)[number]) => (c.focus ? 0 : c.followed ? 1 : 2);
      lane.sort((a, b) => rank(a) - rank(b) || data.cars[String(a.key)].cls.localeCompare(data.cars[String(b.key)].cls) || a.key - b.key);
      ctx.font = "700 9px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const wOf = (t: string) => ctx.measureText(t).width + 10;
      const labels = lane.map((c) => carNo(data, c.key));
      const lk = `${fit.current.k}|${pitTag.x.toFixed(1)},${pitTag.y.toFixed(1)}|${labels.join(",")}|${lane.findIndex((c) => c.focus)}|${overlays.current.panels.map((r) => `${Math.round(r.x)},${Math.round(r.y)},${Math.round(r.w)},${Math.round(r.h)}`).join(";")}`;
      if (pitChips.current?.k !== lk)
        pitChips.current = {
          k: lk,
          l: layoutPitChips({
            outline: outlinePoints(track.pts.map((p) => px(p))),
            W,
            H,
            pitIn: pitTag,
            widths: labels.map(wOf),
            widthOf: wOf,
            max: PIT_CHIPS_MAX,
            clearPx: PIT_CLEAR_PX,
            alongAcross: Math.abs(pitTag.uy) >= Math.abs(pitTag.ux),
            blocked: overlays.current.panels,
          }),
        };
      const l = pitChips.current.l;
      ctx.strokeStyle = C.pit;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(l.leader[0], l.leader[1]);
      ctx.lineTo(l.leader[2], l.leader[3]);
      ctx.stroke();
      if (l.link) {
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(l.link[0], l.link[1]);
        ctx.lineTo(l.link[2], l.link[3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      const counted = lane.length > 0 && !l.boxes.length && !l.more;
      const tagText = counted ? `PIT ${lane.length}` : "PIT";
      ctx.fillStyle = C.pit;
      ctx.beginPath();
      ctx.roundRect(l.tag.x, l.tag.y, l.tag.w, CHIP_H, 4);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.fillText(tagText, l.tag.x + l.tag.w / 2, l.tag.y + 7.5);
      l.boxes.forEach((b, i) => {
        const c = lane[i];
        ctx.fillStyle = C.bg;
        ctx.strokeStyle = c.focus ? TRACE.focus : liveClassColor(data.cars[String(c.key)]?.cls);
        ctx.lineWidth = c.focus ? 2 : 1.5;
        ctx.beginPath();
        ctx.roundRect(b.x, b.y, b.w, CHIP_H, 4);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = C.text;
        ctx.fillText(labels[i], b.x + b.w / 2, b.y + 7.5);
        hits.current.push({ key: c.key, x: b.x + b.w / 2, y: b.y + 7, r: Math.max(10, b.w / 2) });
      });
      if (l.more) {
        const b = l.more;
        ctx.fillStyle = C.bg;
        ctx.strokeStyle = C.pit;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(b.x, b.y, b.w, CHIP_H, 4);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = C.text;
        ctx.fillText(`+${lane.length - l.boxes.length}`, b.x + b.w / 2, b.y + 7.5);
      }
    }

    for (const d of dots) {
      const offTrack = !!d.m.off;
      const [x, y] = offTrack ? px(d.m.offShown ?? d.m.off!) : px(pointAt(track.pts, track.ds, track.len, d.m.shown));
      // The leader is always drawn full size with its number, even when not a compare car.
      const tracked = d.focus || d.followed;
      const r = tracked ? 13 : d.compared || d.leader ? (many && !d.leader ? 8 : 9) : 5;
      // Every dot in its class colour; the focus car and followed cars are the large ones, with a
      // glow and a ring (orange on the focus car, white on the followed ones).
      const color = liveClassColor(data.cars[String(d.key)]?.cls);
      // Overdue with no position: where it is is not known, so it is not drawn (listed under the map).
      if (d.m.flags & OVERDUE) continue;
      // Placed from its lap time (no GPS): an estimate, drawn dashed.
      const estimated = (d.m.flags & EST) !== 0;
      if (estimated && !tracked && hidden.has("estimated")) continue;
      const hollow = !estimated && ((d.m.flags & (NO_GPS | STALE)) !== 0 || isFrozen(d.m, t));
      if (hollow && !tracked && hidden.has("faint")) continue;
      // Fading in onto the map, or out into the pit lane.
      const vis = offTrack ? 1 : visibility(d.m, t, track.len);
      if (vis < 0.02) continue;
      if (d.leader && vis > 0.5 && !hidden.has("leader")) leaderAt = { x, y, r };
      if (tracked) {
        ctx.globalAlpha = 0.3 * vis;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, r + 9, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = (d.other ? 0.3 : d.compared || tracked || d.leader ? 1 : 0.7) * vis;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      if (estimated) {
        const a = ctx.globalAlpha;
        ctx.globalAlpha = a * 0.2;
        ctx.fillStyle = color;
        ctx.fill();
        ctx.globalAlpha = a;
        // Long dashes: plain at a glance on a small dot, so an estimate is not taken for a GPS place.
        ctx.setLineDash([4, 3.5]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (hollow) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (d.ghost) {
        // A lap down or more: a ghost of the dot (see-through, with a thin edge).
        const a = ctx.globalAlpha;
        ctx.globalAlpha = a * 0.3;
        ctx.fillStyle = color;
        ctx.fill();
        ctx.globalAlpha = a * 0.9;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.25;
        ctx.stroke();
        ctx.globalAlpha = a;
      } else {
        ctx.fillStyle = color;
        ctx.fill();
      }
      if (tracked) {
        ctx.strokeStyle = d.focus ? TRACE.focus : "#ffffff";
        ctx.lineWidth = 3;
        ctx.stroke();
      }
      const stoppedNow = !offTrack && isStopped(d.m, t, track.len);
      if (d.outLap && !offTrack && !stoppedNow && !hidden.has("outLap")) {
        // On its out-lap from the pits: a solid cyan ring.
        ctx.strokeStyle = C.outLap;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + 4, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (stoppedNow) {
        // Stopped (frozen fix, next lap overdue): a solid red ring at its last real fix.
        ctx.strokeStyle = C.stopped;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + 4, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (offTrack) {
        // Off the track: a dashed orange ring round the dot.
        ctx.setLineDash([3, 2.5]);
        ctx.strokeStyle = C.offTrack;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, r + 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (tracked || d.compared || d.leader) {
        const label = carNo(data, d.key);
        ctx.fillStyle = hollow || d.ghost ? "#ffffff" : labelOn(color);
        const size = tracked ? (label.length > 3 ? 8 : label.length > 2 ? 9.5 : 11.5) : label.length > 2 ? 7 : 8.5;
        ctx.font = `${tracked ? 700 : 600} ${size}px system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, x, y + 0.5);
      }
      ctx.globalAlpha = 1;
      if (vis > 0.5) hits.current.push({ key: d.key, x, y, r: Math.max(r, 10) });
    }

    // The virtual car of each class (race-laps.ts): its leader's last-laps pace, crossing the line
    // as the clock runs out. A hollow diamond in the class colour.
    if (clock && !hidden.has("virtual")) {
      const ttgNow = clock.frozen ? clock.ttgS : clock.ttgS - (t - clock.at) / 1000;
      for (const v of virtualCars(data, ttgNow, track.len, out)) {
        if ((classView && v.cls !== classView) || hidden.has(`cls:${v.cls}`)) continue;
        const [x, y] = px(pointAt(track.pts, track.ds, track.len, v.d));
        const color = liveClassColor(v.cls);
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(Math.PI / 4);
        ctx.globalAlpha = 0.25;
        ctx.fillStyle = color;
        ctx.fillRect(-6, -6, 12, 12);
        ctx.globalAlpha = 1;
        ctx.setLineDash([2.5, 2]);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(-6, -6, 12, 12);
        ctx.restore();
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 8px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("V", x, y + 0.5);
      }
    }

    // The pit ghosts (pit-ghost.ts): a dashed ring where each car would rejoin after a stop
    // losing its time, with that time: in the focus car's colour for its ghost, white for a
    // followed car's (named).
    for (const g of pitGhostRef.current.on ? ghostsRef.current : []) {
      const gd = ghostOf(g.owner, track.len, g.lossS);
      if (gd === null) continue;
      const [gx, gy] = px(pointAt(track.pts, track.ds, track.len, gd));
      const tone = g.focus ? TRACE.focus : GHOST_FOLLOWED;
      // Its ring: green in clear air, amber in traffic (ghost-traffic.ts).
      const tr = g.traffic;
      ctx.setLineDash([3, 2.5]);
      ctx.strokeStyle = tr ? (tr.near.length ? GHOST_TRAFFIC : GHOST_CLEAR) : tone;
      ctx.lineWidth = g.focus ? 2.5 : 2;
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = tone;
      ctx.beginPath();
      ctx.arc(gx, gy, g.focus ? 11 : 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.stroke();
      ctx.setLineDash([]);
      const text = `${g.focus ? "" : `#${carNo(data, g.owner)} `}PIT +${Math.round(g.lossS)}${tr ? ` · ${ghostLabel(tr)}` : ""}`;
      ctx.font = "700 9px system-ui, sans-serif";
      const w = ctx.measureText(text).width + 8;
      const r = g.focus ? 11 : 9;
      ctx.fillStyle = tone;
      ctx.beginPath();
      ctx.roundRect(gx - w / 2, gy - r - 16, w, 14, 4);
      ctx.fill();
      ctx.fillStyle = "#0b0b18";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, gx, gy - r - 9 + 0.5);
    }

    // Where each car closing on the focus car gets to it within the lap (closing.ts): a tick across
    // the track in how-soon colour, with its number.
    for (const c of closingRef.current.slice(0, 3)) {
      const [mx, my] = px(pointAt(track.pts, track.ds, track.len, c.meetD));
      const [nx, ny] = px(pointAt(track.pts, track.ds, track.len, c.meetD + 20));
      const a = Math.atan2(ny - my, nx - mx) + Math.PI / 2;
      const col = closingColor(c.catchS);
      ctx.strokeStyle = col;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(mx - Math.cos(a) * 12, my - Math.sin(a) * 12);
      ctx.lineTo(mx + Math.cos(a) * 12, my + Math.sin(a) * 12);
      ctx.stroke();
      const text = `#${carNo(data, c.key)}`;
      ctx.font = "700 9px system-ui, sans-serif";
      const w = ctx.measureText(text).width + 8;
      const lx = mx + Math.cos(a) * 24;
      const ly = my + Math.sin(a) * 24;
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.roundRect(lx - w / 2, ly - 7, w, 14, 4);
      ctx.fill();
      ctx.fillStyle = "#0b0b18";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, lx, ly + 0.5);
    }

    // The overall leader: a gold ring round its dot and a P1 tag above it, on top of everything.
    if (leaderAt) {
      const { x, y, r } = leaderAt;
      ctx.strokeStyle = C.leader;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(x, y, r + 3.5, 0, Math.PI * 2);
      ctx.stroke();
      const ty = y - r - 13;
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = C.leader;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x - 11, ty - 7, 22, 14, 7);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#0b0b18";
      ctx.font = "800 9.5px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("P1", x, ty + 0.5);
    }

    // The safety car under a caution, only where the timing places it (a car of its own, "SC" or
    // "PC"). RaceTrace has no position for it otherwise, so it is not drawn: an estimate was too
    // often in the wrong place (owner's call, Oct 28 2026).
    const own = isCaution(flag) ? findSafetyCar(motions.current) : null;
    const sc: { d: number; estimated: boolean } | null = own ? { d: own.shown, estimated: false } : null;
    if (sc && !hidden.has("sc")) {
      const [x, y] = px(pointAt(track.pts, track.ds, track.len, sc.d));
      ctx.fillStyle = "#fab219";
      ctx.strokeStyle = "#0b0b18";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(x - 13, y - 8, 26, 16, 4);
      ctx.fill();
      ctx.stroke();
      if (sc.estimated) {
        ctx.setLineDash([3, 2]);
        ctx.strokeStyle = "#fab219";
        ctx.beginPath();
        ctx.roundRect(x - 16, y - 11, 32, 22, 6);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.fillStyle = "#0b0b18";
      ctx.font = "700 10px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("SC", x, y + 0.5);
    }
  };
  useEffect(() => {
    if (!track) return;
    let raf = 0;
    const frame = () => {
      draw.current();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [track]);

  // Turning the map: press and drag round its centre with the mouse (or a pen). Snaps to every
  // 45° within SNAP_DEG; Shift turns it freely. Touch screens use the buttons (a finger drag
  // scrolls the page).
  const centreAngle = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return (Math.atan2(e.clientY - r.top - r.height / 2, e.clientX - r.left - r.width / 2) * 180) / Math.PI;
  };
  const hitAt = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    return [...hits.current].reverse().find((h) => Math.hypot(h.x - x, h.y - y) <= h.r);
  };
  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === "touch" || e.button !== 0) return;
    drag.current = { id: e.pointerId, startA: centreAngle(e), base: angleRef.current, x: e.clientX, y: e.clientY, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    const el = e.currentTarget;
    if (!d || d.id !== e.pointerId) {
      if (e.pointerType !== "touch") el.style.cursor = hitAt(e) ? "pointer" : "grab";
      return;
    }
    if (!d.moved) {
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) < 5) return;
      d.moved = true;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = "grabbing";
    }
    angleRef.current = snapDeg(d.base + centreAngle(e) - d.startA, e.shiftKey);
    if (readout.current) readout.current.textContent = `${Math.round(angleRef.current)}°`;
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (!d.moved) return;
    dragged.current = true;
    e.currentTarget.style.cursor = "grab";
    turnTo(angleRef.current);
  };

  const onClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (dragged.current) {
      dragged.current = false;
      return;
    }
    // The top-most dot under the tap (the focus car is drawn last).
    const hit = hitAt(e);
    // Follow the car tapped, or stop following it (the focus car is always shown large).
    if (hit && hit.key !== focusNum) follow(toggleFollow(followed, carNo(data, hit.key)));
  };

  const age = readingAt !== null ? now - readingAt : null;
  const keys = (nums: string[]) => nums.map((n) => carKey(data, n)).filter((k): k is number => k !== null && !!data.cars[String(k)]);
  const leader = overallLeader(data);
  const leaderInPits = leader !== null && keys(pits).includes(leader) ? leader : null;
  // Cars stopped now (live-field.ts stoppedCars: by GPS with a fix, by laps without), each second.
  // A car listed with no lap and no position (OVERDUE) is not listed again as stopped.
  const stopped = [...f.stopped].map((k) => carNo(data, k)).filter((n) => !overdue.includes(n));
  const label = (nums: string[]) =>
    keys(nums)
      .map((k) => `#${carNo(data, k)}`)
      .join(", ");
  const mapClasses = Object.keys(data.classGroups).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const leaderNote = leaderInPits ? `overall leader #${carNo(data, leaderInPits)} in the pits` : "overall leader";
  const mapNotes = [
    mapNote ?? "",
    label(unplaced) ? `No position: ${label(unplaced)}` : "",
    label(stopped) ? `Stopped: ${label(stopped)}` : "",
    label(overdue) ? `No lap, no position: ${label(overdue)}` : "",
    label(lostSignal) ? `Lost signal: ${label(lostSignal)}` : "",
    label(offTrack) ? `Off track: ${label(offTrack)}` : "",
    age !== null && age > OLD_MS ? `Positions ${Math.round(age / 1000)} s old` : "",
  ].filter(Boolean);
  // Every running car's place on the track now (refreshed with the clock every second), and every
  // car in the pit lane, also the ones the map no longer moves (it drops a car once in the pits).
  const places: TrackPlace[] = carPlaces(data, motions.current, out, pitVisits);
  // The pit ghosts: where the focus car, and the first followed cars (MAX_GHOSTS), would rejoin
  // after a stop, among the places (the Live Crossing Order lists each in its place on the road).
  // The focus car's loses the time the viewer set; a followed car's its own class's green stop.
  // The traffic each would come out in, and when it next comes out clear (ghost-traffic.ts): each
  // car on track at its recent green pace (its motion's pace without one).
  const ghosts: GhostShown[] = [];
  let box: BoxView | null = null;
  if (track && pitGhost.on) {
    const speedOf = (k: number) => {
      const p = greenPace.get(k);
      if (p && p > 0) return track.len / p;
      const m = [...motions.current.values()].find((x) => carKey(data, x.num) === k);
      return m && m.base > 0 ? m.base : typicalSpeed;
    };
    const owners = [focusNum, ...[...followKeys].filter((k) => k !== focusNum)].slice(0, MAX_GHOSTS);
    for (const [i, owner] of owners.entries()) {
      const focus = owner === focusNum;
      const loss = focus ? lossS : ghostLoss({ ...pitGhost, lossS: null }, stopFigure(owner));
      const d = ghostOf(owner, track.len, loss);
      if (d === null) continue;
      const traffic = ghostTraffic({
        cars: places.filter((p) => !p.inPit && p.key !== owner).map((p) => ({ key: p.key, d: p.d, v: speedOf(p.key) })),
        ghostD: d,
        ghostV: speedOf(owner),
        len: track.len,
        windowS: pitGhost.windowS,
        caution: isCaution(flag),
        ...(focus ? {} : { stepS: FOLLOWED_STEP_S }),
      });
      ghosts.push({ key: ghostKey(i), owner, focus, lossS: loss, d, traffic });
    }
    // The box call for the focus car (box-call.ts): when it next reaches the pit-in point, against
    // its pit window and the traffic its ghost would come out in by then.
    const fm = [...motions.current.values()].find((x) => carKey(data, x.num) === focusNum);
    const lane = pitLane.current;
    if (fm && !out.has(focusNum)) {
      if (fm.flags & IN_PIT || pitVisits.has(focusNum)) box = { note: "In the pit lane" };
      else if (fm.pitIn != null) box = { note: "Pitting" };
      else if (!lane) box = { note: "Pit entry not known yet: it is learned from the cars' pit flags" };
      else if (focusPlan?.car.stopsToGo === 0) box = { note: "No stop needed to the flag" };
      else {
        const fg = ghosts.find((g) => g.focus);
        const cars = places.filter((p) => !p.inPit && p.key !== focusNum).map((p) => ({ key: p.key, d: p.d, v: speedOf(p.key) }));
        const caution = isCaution(flag);
        const entryS = secondsToPitEntry(fm, lane, track.len);
        const call =
          entryS === null
            ? null
            : boxCall({
                entryS,
                // Just over the line: the next one is a lap away.
                lineS: (ahead(fm.shown, 0, track.len) || track.len) / fm.v,
                lapS: track.len / fm.v,
                lapsDone: Math.max(focusPlan?.car.laps ?? 0, fm.laps),
                window: focusPlan?.window ?? null,
                callEarlyS: pitGhost.callEarlyS,
                nearAt: (t) =>
                  caution || !fg ? null : trafficAt({ cars, ghostD: fg.d, ghostV: speedOf(focusNum), len: track.len, windowS: pitGhost.windowS, t }).length,
              });
        box = call ? { call } : { note: "No pace for it yet" };
      }
    }
  }
  ghostsRef.current = ghosts;
  for (const g of ghosts) places.push({ key: g.key, d: g.d, inPit: false });
  // The cars that will catch the focus car within its next lap (closing.ts): listed on the map,
  // and where each gets there marked on the track.
  const closing = track ? closingOnFocus({ places, focusKey: focusNum, len: track.len, pace: (k) => greenPace.get(k) ?? null, caution: isCaution(flag) }) : [];
  closingRef.current = closingOn && closingMarks ? closing : [];
  const closingShown = !!track && !isCaution(flag) && places.some((p) => p.key === focusNum && !p.inPit) && greenPace.get(focusNum) != null;
  /** The box call and closing panels at the map's bottom left, and their height: the key stops above them. */
  const stackRef = useRef<HTMLDivElement>(null);
  const [stackH, setStackH] = useState(0);
  const stackShown = (closingShown && closingOn) || (!!box && !compact);
  useEffect(() => {
    const el = stackRef.current;
    if (!el || !stackShown) {
      setStackH(0);
      return;
    }
    const ro = new ResizeObserver(() => setStackH(el.getBoundingClientRect().height));
    ro.observe(el);
    setStackH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [stackShown]);

  return (
    <div className="flex flex-col md:min-h-0 md:flex-1">
      <div className="flex flex-col md:min-h-0 md:flex-1 md:flex-row">
        <div className="flex flex-col md:min-h-0 md:min-w-0 md:flex-1">
        {/* The map, with its key and the pit / not-running cars: on a wide screen as chips floating
            over the map (key left, pits right) so the map runs edge to edge, under it otherwise. */}
        <div className="flex flex-col md:min-h-0 md:flex-1 xl:relative">
        <div ref={wrapRef} className="relative h-[80vw] max-h-[560px] min-h-[300px] md:h-auto md:max-h-none md:min-h-0 md:min-w-0 md:flex-1" style={{ background: C.bg }}>
          <canvas
            ref={canvasRef}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            role="img"
            aria-label={`Track map: every car's place on the circuit, ${focusNum ? `focus car #${carNo(data, focusNum)} highlighted` : ""}. Tap a car to follow it; drag to turn the map.`}
            className="absolute inset-0 cursor-grab"
          />
          {track && <TurnButtons angle={angle} readout={readout} onTurn={turnTo} />}
          {track && <FlagBadge flag={flag} pits={focusPits && focusCls ? { open: focusPits.open, short: focusPits.short, all: !!focusPits.all, cls: focusCls } : null} />}
          {((closingShown && closingOn) || (box && !compact)) && (
            <div ref={stackRef} data-map-overlay className="pointer-events-none absolute bottom-2 left-2 flex max-w-[calc(100%-7rem)] flex-col items-start gap-1">
              {box && !compact && <BoxCallPanel num={carNo(data, focusNum)} box={box} />}
              {closingShown && closingOn && (
                <ClosingPanel
                  data={data}
                  focusNum={focusNum}
                  closing={closing}
                  onMap={closingMarks}
                  onToggleMap={toggleClosingMarks}
                  onClose={() => toggleLayer("closing")}
                  followed={followKeys}
                  onCarTap={(k) => follow(toggleFollow(followed, carNo(data, k)))}
                />
              )}
            </div>
          )}
          {!track && (
            <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm" style={{ color: C.muted }}>
              {trackMissing ? "The track map appears once the timing has learned the circuit from a few clean laps." : "Loading the track map…"}
            </p>
          )}
        </div>
        {/* On a phone the box call sits under the map, so it never covers the track. */}
        {compact && box && <BoxCallPanel num={carNo(data, focusNum)} box={box} strip />}
        {compact ? (
          <PhoneMapBar
            data={data}
            pits={pitVisits}
            out={out}
            focusNum={focusNum}
            setFocusNum={setFocusNum}
            followed={followed}
            onFollow={follow}
            hiddenCount={hidden.size}
            notes={mapNotes}
            mapKey={
              <MapKey
                className="border-t-0 !px-0 !py-0"
                classes={mapClasses}
                hidden={hidden}
                onToggle={toggleLayer}
                virtual={!!clock}
                pitGhost={pitGhost}
                onPitGhost={changePitGhost}
                ghostLossS={lossS}
                measuredLossS={measuredLossS}
                caution={isCaution(flag) && !!findSafetyCar(motions.current)}
                leaderNote={leaderNote}
                status={[]}
              />
            }
          />
        ) : (
          <>
            <MapKey
              classes={mapClasses}
              hidden={hidden}
              onToggle={toggleLayer}
              virtual={!!clock}
              pitGhost={pitGhost}
              onPitGhost={changePitGhost}
              ghostLossS={lossS}
              measuredLossS={measuredLossS}
              caution={isCaution(flag) && !!findSafetyCar(motions.current)}
              leaderNote={leaderNote}
              status={mapNotes}
              below={stackH ? stackH + 8 : 0}
            />
            <PitList data={data} pits={pitVisits} out={out} focusNum={focusNum} setFocusNum={setFocusNum} />
          </>
        )}
        </div>
        {!compact && <FollowBar data={data} focusNum={focusNum} followed={followed} out={out} onChange={follow} />}
        <RaceControl slug={slug} data={data} focusNum={focusNum} followed={followKeys} {...(control !== undefined ? { source: control } : {})} />
        </div>
        <RunningOrder
          above={
            <div role="tablist" aria-label="Running order view" className="mb-1.5 flex gap-1 text-xs">
              {(
                [
                  ["timing", "Running Order"],
                  ["live", "Live Crossing Order"],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={orderView === v}
                  title={
                    v === "timing"
                      ? "Running Order: the race order from the timing, as of each car's last crossing of the line. Tap a car to follow it on the map."
                      : "Live Crossing Order: every car in the order it is round the circuit now, ahead of and behind the focus car on the road, whatever lap each is on."
                  }
                  onClick={() => pickOrderView(v)}
                  className="min-h-[44px] rounded-md px-3 text-[13px] font-semibold sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-xs"
                  style={{ background: orderView === v ? "#2a2a48" : "transparent", color: orderView === v ? C.text : C.muted }}
                >
                  {label}
                </button>
              ))}
            </div>
          }
          swap={
            orderView === "live" && track ? (
              (() => {
                const motionOf = (k: number | null) => [...motions.current.values()].find((m) => carKey(data, m.num) === k);
                const onTrack = (k: number | null) => places.some((p) => p.key === k && !p.inPit);
                // From the leader when the viewer picks it, and for now when the focus car is in the
                // pits or has no place on the map (the list from the focus car needs its place).
                const focusAway = !onTrack(focusNum);
                const fromLeader = crossFrom === "leader" || focusAway;
                const leaderK = overallLeader(data);
                let rows;
                let speed = typicalSpeed;
                if (fromLeader) {
                  const place = (k: number) => data.cars[String(k)]?.finishPos ?? 0;
                  // The gaps are the focus car's when it is on track: at its pace (else the car measured from).
                  const m = motionOf(focusAway ? leaderRef(places, leaderK, place) : focusNum);
                  speed = m && m.base > 0 ? m.base : typicalSpeed;
                  rows = leaderOrder(places, leaderK, place, track.len, speed);
                } else {
                  const m = motionOf(focusNum);
                  speed = m && m.base > 0 ? m.base : typicalSpeed;
                  rows = trackOrder(places, focusNum, track.len, speed);
                }
                const note =
                  crossFrom === "focus" && focusAway
                    ? `#${carNo(data, focusNum)} ${out.has(focusNum) ? "is not running" : pitVisits.has(focusNum) ? "is in the pits" : "has no place on the track right now"}: from the leader${out.has(focusNum) ? "" : " until it is back on track"}.`
                    : null;
                return (
                  <LiveTrackOrder
                    data={data}
                    rows={rows}
                    lapsDown={lapped}
                    drivers={drivers}
                    followed={followKeys}
                    refMissing={!rows.length}
                    focusKey={focusNum}
                    leaderKey={leaderK}
                    from={fromLeader ? "leader" : "focus"}
                    onFrom={pickCrossFrom}
                    note={note}
                    ghosts={ghosts}
                    boxNow={boxNow}
                    onRowTap={(k) => {
                      if (k !== focusNum) follow(toggleFollow(followed, carNo(data, k)));
                    }}
                  />
                );
              })()
            ) : undefined
          }
          data={data}
          focusNum={focusNum}
          setFocusNum={setFocusNum}
          compSet={compSet}
          classView={classView}
          annotations={annotations}
          pits={pitVisits}
          out={out}
          followed={followKeys}
          onRowTap={(k) => {
            if (k !== focusNum) follow(toggleFollow(followed, carNo(data, k)));
          }}
        />
      </div>
    </div>
  );
}


/** The overall leader now (the timing's running order), if it has one. */
export function overallLeader(data: RaceChartData): number | null {
  for (const [k, c] of Object.entries(data.cars)) if (c.finishPos === 1) return Number(k);
  return null;
}

/**
 * A car on its out-lap: its last completed lap is the one it pitted on (the chart's pit flag sits
 * on the in-lap), and it is out of the pit lane now. Ends when it completes the lap.
 */
export function onOutLap(data: RaceChartData, key: number, inPits: Map<number, PitVisit>): boolean {
  const laps = data.cars[String(key)]?.laps ?? [];
  return laps.length > 0 && laps[laps.length - 1].pit === 1 && !inPits.has(key);
}

/** What the map leaves out (the key's chips), kept on this device. */
const HIDDEN_KEY = "rt.live.mapHidden";
/** The pit ghost's settings in the map key: "open" when shown (folded away until then). */
const GHOST_OPEN_KEY = "rt.live.pitGhostOpen";
function readHiddenLayers(): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? "[]");
    const s = new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
    // The cars-closing panel had its own off switch ("rt.live.closingPanel"): turned off there, it
    // stays off, now as the key's chip.
    if (localStorage.getItem("rt.live.closingPanel") === "off") {
      s.add("closing");
      localStorage.removeItem("rt.live.closingPanel");
      saveHiddenLayers(s);
    }
    return s;
  } catch {
    return new Set();
  }
}
function saveHiddenLayers(s: Set<string>) {
  try {
    if (s.size) localStorage.setItem(HIDDEN_KEY, JSON.stringify([...s]));
    else localStorage.removeItem(HIDDEN_KEY);
  } catch {
    /* private window: for this visit only */
  }
}

/**
 * The map's key, under the map: a chip for each class and each marker, like the compare chips.
 * Tapping one hides it on the map (the chip dims) or shows it again. A class's cars, lapped cars and
 * cars with a faint signal are left off the map (the focus car and followed cars always stay); the
 * P1 tag, the virtual cars, the out-lap rings and the safety car are left off. Then what the map
 * cannot place right now (no position, stopped, off track).
 */
function MapKey({
  className = "",
  classes,
  hidden,
  onToggle,
  virtual,
  pitGhost,
  onPitGhost,
  ghostLossS,
  measuredLossS,
  caution,
  leaderNote,
  status,
  below = 0,
}: {
  className?: string;
  /** On a wide screen, room (px) to leave under the key for the box call and closing panels at the map's bottom left. */
  below?: number;
  classes: string[];
  hidden: Set<string>;
  onToggle: (k: string) => void;
  virtual: boolean;
  /** The pit ghost: off until turned on, with the time a stop would lose. */
  pitGhost: PitGhost;
  onPitGhost: (g: PitGhost) => void;
  /** The time the ghost uses now, and this race's measured green-flag stop (its time unless one is typed). */
  ghostLossS: number;
  measuredLossS: number | null;
  caution: boolean;
  leaderNote: string;
  status: string[];
}) {
  // The pit ghost's settings shown or folded away (kept on this device): folded until opened, so
  // the key stays small and the map as large as it can be.
  const [ghostOpen, setGhostOpen] = useState(() => {
    try {
      return localStorage.getItem(GHOST_OPEN_KEY) === "open";
    } catch {
      return false;
    }
  });
  const toggleGhostOpen = () =>
    setGhostOpen((o) => {
      try {
        localStorage.setItem(GHOST_OPEN_KEY, o ? "closed" : "open");
      } catch {
        // not kept: the setting holds for this visit
      }
      return !o;
    });
  const chip = (k: string, swatch: React.ReactNode, text: string, title?: string) => {
    const off = hidden.has(k);
    return (
      <button
        key={k}
        type="button"
        aria-pressed={!off}
        onClick={() => onToggle(k)}
        title={`${title ? `${title} ` : ""}${off ? "Hidden: tap to show it on the map." : "Tap to hide it on the map."}`}
        className="pointer-events-auto flex min-h-[44px] w-fit items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 hover:bg-white/5 sm:min-h-0 sm:px-2 sm:py-0.5 xl:backdrop-blur-sm"
        style={{ borderColor: off ? "#2a2a48" : "#3a3a5c", color: off ? "#5c5c78" : C.text, textDecoration: off ? "line-through" : undefined, background: OVER_BG }}
      >
        <span className="flex items-center" style={{ opacity: off ? 0.35 : 1 }}>
          {swatch}
        </span>
        {text}
      </button>
    );
  };
  const dot = (bg: string, extra = "") => <span className={`inline-block h-2.5 w-2.5 rounded-full ${extra}`} style={{ background: bg }} />;
  return (
    <div
      data-map-overlay
      className={`custom-scroll flex flex-wrap items-center gap-1.5 border-t px-3 py-2 text-xs sm:px-6 xl:pointer-events-none xl:absolute xl:left-2 xl:top-2 xl:max-h-[calc(100%-1rem-var(--below,0px))] xl:max-w-[16rem] xl:flex-col xl:flex-nowrap xl:items-start xl:overflow-y-auto xl:border-0 xl:p-0 xl:[&>*]:shrink-0 ${className}`}
      style={{ borderColor: "#1e1e38", color: C.muted, ["--below" as string]: `${below}px` }}
    >
      {classes.map((cls) => chip(`cls:${cls}`, dot(liveClassColor(cls)), cls, `${cls} cars.`))}
      {chip(
        "leader",
        <span className="rounded-full border-[1.5px] bg-white px-1.5 text-[9.5px] font-extrabold leading-[13px]" style={{ borderColor: C.leader, color: C.bg }}>
          P1
        </span>,
        leaderNote,
        "The overall leader's tag."
      )}
      {virtual &&
        chip(
          "virtual",
          <span className="inline-block h-2.5 w-2.5 rotate-45 border-2 border-dashed" style={{ borderColor: C.muted }} />,
          "virtual car",
          "Each class's virtual car laps at its class leader's average over its last 3 laps and crosses the line exactly as the clock runs out: a leader ahead of it on the road has one more lap to run."
        )}
      {/* The pit ghost: off until turned on (unlike the rest), with the time a stop would lose. */}
      <span className="pointer-events-auto flex w-fit max-w-full flex-wrap items-center gap-1 whitespace-nowrap rounded-md border xl:backdrop-blur-sm" style={{ borderColor: pitGhost.on ? TRACE.focus : "#2a2a48", background: OVER_BG }}>
        <button
          type="button"
          aria-pressed={pitGhost.on}
          onClick={() => onPitGhost({ ...pitGhost, on: !pitGhost.on })}
          title={`Pit ghost: where the focus car would rejoin if it stopped now and lost the time set (pit lane plus fuelling), on the track and in the Live Crossing Order; and, in white, where the first ${MAX_GHOSTS - 1} cars you follow would, each losing its class's green-flag stop. With it on, the box call over the map says when to bring the focus car in. ${pitGhost.on ? "Tap to turn it off." : "Tap to turn it on."}`}
          className="flex items-center gap-1.5 rounded-md py-0.5 pl-2 pr-1 hover:bg-white/5"
          style={{ color: pitGhost.on ? C.text : "#5c5c78" }}
        >
          <span className="inline-block h-3 w-3 rounded-full border-2 border-dashed" style={{ borderColor: TRACE.focus, opacity: pitGhost.on ? 1 : 0.5 }} />
          pit ghost
        </button>
        {/* The stop's time on the first line, folded or not: changed without opening the settings. */}
        {pitGhost.on && (
          <label
            className="flex items-center gap-0.5 pr-1.5"
            style={{ color: C.muted }}
            title={`The time a stop would lose (s): pit lane plus fuelling. ${
              measuredLossS !== null ? `This race's green-flag stops lose about ${Math.round(measuredLossS)} s` : `No green-flag stop measured yet: ${FALLBACK_LOSS_S} s until there is one`
            }${pitGhost.lossS === null ? " (in use: type a time to set your own)." : "; you set your own."}`}
          >
            +
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={GHOST_MAX_S}
              step={1}
              value={pitGhost.lossS ?? Math.round(ghostLossS)}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= 0 && v <= GHOST_MAX_S) onPitGhost({ ...pitGhost, lossS: v });
              }}
              aria-label="Pit ghost: seconds a stop would lose"
              className="w-11 rounded border bg-transparent px-1 text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
              style={{ borderColor: "#3a3a5c", color: pitGhost.lossS === null ? C.muted : C.text }}
            />
            s
          </label>
        )}
        {pitGhost.on &&
          (pitGhost.lossS === null ? (
            <span className="pr-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.muted }} title="The race's measured green-flag stop: type a time to set your own">
              auto
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onPitGhost({ ...pitGhost, lossS: null })}
              className="mr-1 rounded border px-1 text-[10px] font-semibold uppercase tracking-wider hover:bg-white/10"
              style={{ borderColor: "#3a3a5c", color: C.muted }}
              title={measuredLossS !== null ? `Back to this race's measured green-flag stop (about ${Math.round(measuredLossS)} s)` : "Back to the race's measured green-flag stop once there is one"}
              aria-label="Pit ghost: use the race's measured stop time"
            >
              auto
            </button>
          ))}
        {pitGhost.on && (
          // Its own button, apart from the on/off one: shows or hides the settings (kept on this device).
          <button
            type="button"
            aria-expanded={ghostOpen}
            onClick={toggleGhostOpen}
            title={ghostOpen ? "Hide the pit ghost's settings (it stays on)" : "Show the pit ghost's settings: clear air and when to call"}
            aria-label={ghostOpen ? "Hide the pit ghost settings" : "Show the pit ghost settings"}
            className="ml-auto flex min-h-[44px] min-w-[44px] items-center justify-center self-stretch rounded-r-md border-l hover:bg-white/10 sm:min-h-[26px] sm:min-w-[32px]"
            style={{ borderColor: "#3a3a5c", color: C.text }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden style={{ transform: ghostOpen ? "rotate(180deg)" : undefined, transition: "transform 120ms" }}>
              <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        {pitGhost.on && ghostOpen && <span aria-hidden className="h-0 basis-full" />}
        {pitGhost.on && ghostOpen && (
          <label className="flex items-center gap-0.5 pr-1.5" style={{ color: C.muted }} title="Clear air: no car within this many seconds either side of where it would rejoin">
            clear ±
            <input
              type="number"
              inputMode="decimal"
              min={0.5}
              max={WINDOW_MAX_S}
              step={0.5}
              value={pitGhost.windowS}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v > 0 && v <= WINDOW_MAX_S) onPitGhost({ ...pitGhost, windowS: v });
              }}
              aria-label="Pit ghost: seconds of clear air either side"
              className="w-9 rounded border bg-transparent px-1 text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
              style={{ borderColor: "#3a3a5c", color: C.text }}
            />
            s
          </label>
        )}
        {pitGhost.on && ghostOpen && (
          <label className="flex items-center gap-0.5 pl-2 pr-1.5" style={{ color: C.muted }} title="Box call: make the call this many seconds before the pit-in point (time for the driver to hear it, and the limiter zone before it)">
            call
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={CALL_EARLY_MAX_S}
              step={1}
              value={pitGhost.callEarlyS}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= 0 && v <= CALL_EARLY_MAX_S) onPitGhost({ ...pitGhost, callEarlyS: v });
              }}
              aria-label="Box call: seconds before the pit-in point"
              className="w-8 rounded border bg-transparent px-1 text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
              style={{ borderColor: "#3a3a5c", color: C.text }}
            />
            s early
          </label>
        )}
      </span>
      {chip(
        "pitLane",
        <span className="inline-block h-3 w-3 rounded-full border-2 border-dashed" style={{ borderColor: C.pit }} />,
        "in the pits",
        "In the pit lane or on the pit road on its way in or out: drawn beside the track where it is, dashed, and counted under PIT. The pit road (faint dots) is learned from the cars' own paths in and out."
      )}
      {chip("lapped", <span className="inline-block h-2.5 w-2.5 rounded-full border" style={{ borderColor: C.muted, background: "rgba(154,154,180,0.3)" }} />, "lapped", "A lap or more down on its class leader (the LD column).")}
      {chip("outLap", <span className="inline-block h-3 w-3 rounded-full border-2" style={{ borderColor: C.outLap }} />, "out-lap", "Back on track from a pit stop, on the lap it rejoined.")}
      {chip("faint", <span className="inline-block h-2.5 w-2.5 rounded-full border" style={{ borderColor: C.muted }} />, "faint signal", "No fresh position from the car.")}
      {chip(
        "estimated",
        <span className="inline-block h-2.5 w-2.5 rounded-full border-2 border-dashed" style={{ borderColor: C.muted }} />,
        "estimated",
        "No GPS: placed by its time into the lap over its projected lap time. An estimate: never shown stopped; once its lap is overdue it leaves the map (listed under it)."
      )}
      {chip(
        "closing",
        <span className="inline-block h-3 w-[3px] rounded-sm" style={{ background: "#fab219" }} />,
        "closing on focus car",
        "The cars that will catch the focus car within its next lap: listed on the map (bottom left) with where each catches it marked on the track. Tap to hide or show it all."
      )}
      {caution &&
        chip(
          "sc",
          <span className="rounded px-1 text-[10px] font-bold" style={{ background: "#fab219", color: "#0b0b18" }}>
            SC
          </span>,
          "safety car"
        )}
      {status.map((t) => (
        <span key={t} className="ml-1 xl:ml-0 xl:max-w-44 xl:rounded xl:px-1.5 xl:py-0.5" style={{ background: OVER_BG }}>
          {t}
        </span>
      ))}
    </div>
  );
}

/** Behind a chip floating over the map on a wide screen: the map's ground, mostly opaque. */
const OVER_BG = "rgba(11,11,24,0.82)";

/** How soon a car closing on the focus car gets to it, as a colour: red within URGENT_S, amber within SOON_S. */
function closingColor(catchS: number): string {
  return catchS < URGENT_S ? "#f87171" : catchS < SOON_S ? "#fab219" : "#cbd5e1";
}

/** "now", "8 s", "1:05". */
function soonText(s: number): string {
  if (s < 1) return "now";
  if (s < 60) return `${Math.round(s)} s`;
  const r = Math.round(s);
  return `${Math.floor(r / 60)}:${String(r % 60).padStart(2, "0")}`;
}

/**
 * On the map, bottom left: the cars that will catch the focus car within its next lap (closing.ts),
 * soonest first, each with how long until it gets there; its place on the track is marked on the
 * map. One quiet line when no one is closing.
 */
function ClosingPanel({
  data,
  focusNum,
  closing,
  onMap,
  onToggleMap,
  onClose,
  followed,
  onCarTap,
}: {
  data: RaceChartData;
  focusNum: number;
  closing: Closing[];
  /** Where each car gets there is marked on the track. */
  onMap: boolean;
  onToggleMap: () => void;
  /** Hide the feature (the map key's "closing on focus car" chip brings it back). */
  onClose: () => void;
  /** The cars followed on the map; tapping a car follows it or stops (as a row of the timing table). */
  followed: Set<number>;
  onCarTap: (key: number) => void;
}) {
  const iconBtn = "flex h-5 w-5 items-center justify-center rounded hover:bg-white/10";
  const shown = closing.slice(0, 4);
  const num = carNo(data, focusNum);
  const tip =
    "Cars on track that will catch the focus car within its next lap, at each car's pace over its last 5 green laps: how long until each gets there (red within 10 s, amber within 30 s), and where, marked on the track. Faster classes lapping it count too. Not shown under a caution.";
  return (
    <div className="pointer-events-auto max-w-full rounded-md border text-[11px] backdrop-blur-sm" style={{ borderColor: "#3a3a5c", background: OVER_BG, color: C.text }} role="status" aria-label={`Closing on #${num}`}>
      <div className="flex items-center gap-1 px-2 pt-1 font-semibold uppercase tracking-wider" style={{ color: closing.length ? "#fab219" : C.muted, paddingBottom: closing.length ? 2 : 4 }}>
        {closing.length ? `Closing on #${num}` : "No one closing"}
        <InfoTip label="Closing on the focus car" text={tip} more={PART_HELP.closing} />
        <span className="ml-auto flex items-center gap-0.5 pl-2">
          <button
            type="button"
            onClick={onToggleMap}
            aria-pressed={onMap}
            className={iconBtn}
            style={{ color: onMap ? "#fab219" : C.muted }}
            title={onMap ? "Hide where they catch it on the track" : "Mark where they catch it on the track"}
            aria-label={onMap ? "Hide the marks on the track" : "Mark them on the track"}
          >
            {/* A tick across a stretch of track. */}
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeLinecap="round" aria-hidden>
              <path d="M2 11c3-5 9-5 12 0" strokeWidth={2.5} opacity={0.45} />
              <path d="M8 3v9" strokeWidth={2.2} />
              {!onMap && <path d="M2 2l12 12" strokeWidth={1.6} />}
            </svg>
          </button>
          <button type="button" onClick={onClose} className={iconBtn} style={{ color: C.muted }} title="Hide the cars closing (show it again from the map key)" aria-label="Hide the cars closing">
            <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden>
              <path d="M2 2l8 8M10 2l-8 8" />
            </svg>
          </button>
        </span>
      </div>
      {shown.length > 0 && (
        <ul className="m-0 list-none px-1 pb-1">
          {shown.map((c) => {
            const car = data.cars[String(c.key)];
            const on = followed.has(c.key);
            return (
              <li key={c.key}>
                <button
                  type="button"
                  onClick={() => onCarTap(c.key)}
                  aria-pressed={on}
                  className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left tabular-nums hover:bg-white/10"
                  style={{ background: on ? "rgba(255,255,255,0.08)" : undefined }}
                  title={`#${carNo(data, c.key)} is ${c.behindS.toFixed(1)} s behind on the road and ${c.quickerS.toFixed(1)} s a lap quicker: it gets to #${num} in ${soonText(c.catchS)}. ${on ? "Following on the map: tap to stop." : "Tap to follow it on the map."}`}
                >
                  <span className="rounded-sm border-l-[3px] pl-1 font-bold" style={{ borderColor: liveClassColor(car?.cls ?? "") }}>
                    {on ? <span className="rounded-full border-2 border-white px-1">#{carNo(data, c.key)}</span> : `#${carNo(data, c.key)}`}
                  </span>
                  <span style={{ color: C.muted }}>{car?.cls}</span>
                  <span className="ml-auto font-bold" style={{ color: closingColor(c.catchS) }}>
                    {soonText(c.catchS)}
                  </span>
                </button>
              </li>
            );
          })}
          {closing.length > shown.length && <li className="px-1 text-[10px]" style={{ color: C.muted }}>+{closing.length - shown.length} more</li>}
        </ul>
      )}
    </div>
  );
}

/** The box call shown: the call, or why there is none ("In the pit lane"). */
type BoxView = { call: BoxCall } | { note: string };

const BOX_TONE: Record<BoxTone, string> = { go: "#4ade80", wait: "#fab219", late: "#f87171", info: "#cbd5e1" };

/**
 * When to box the focus car (box-call.ts), with the pit ghost on: over the map, bottom left, on a
 * wide screen; on a phone a strip under the map (`strip`), so it never covers the track. The call
 * is made the map key's "call … s early" before the pit-in point.
 */
function BoxCallPanel({ num, box, strip = false }: { num: string; box: BoxView; strip?: boolean }) {
  const tone = "call" in box ? BOX_TONE[box.call.tone] : C.muted;
  const tip =
    "When to box the focus car: the seconds until it reaches the pit-in point at its pace, the call that many seconds earlier (the map key's \"call … s early\"), whether the lap is inside its pit window (Strategy tab), and whether its pit ghost would come out in clear air then (the ghost's clear ± setting). An estimate.";
  const label = (
    <span className="flex shrink-0 items-center gap-1 font-semibold uppercase tracking-wider" style={{ color: C.muted }}>
      Box call #{num}
      <InfoTip label="Box call" text={tip} more={PART_HELP.boxCall} />
    </span>
  );
  if (strip)
    return (
      <div className="flex flex-wrap items-baseline gap-x-2 border-t px-3 py-1.5 text-xs" style={{ borderColor: "#2a2a48", borderLeft: `3px solid ${tone}`, background: C.bg, color: C.text }} role="group" aria-label={`Box call for #${num}`}>
        {label}
        {"call" in box ? (
          <>
            <span className="font-bold" style={{ color: tone }}>
              {box.call.head}
            </span>
            <span className="tabular-nums">{box.call.detail}</span>
          </>
        ) : (
          <span style={{ color: C.muted }}>{box.note}</span>
        )}
      </div>
    );
  return (
    <div className="pointer-events-auto max-w-full rounded-md border text-[11px] backdrop-blur-sm" style={{ borderColor: "call" in box ? tone : "#3a3a5c", background: OVER_BG, color: C.text }} role="group" aria-label={`Box call for #${num}`}>
      <div className="px-2 pt-1">{label}</div>
      {"call" in box ? (
        <div className="px-2 pb-1">
          <p className="text-[13px] font-bold" style={{ color: tone }}>
            {box.call.head}
          </p>
          <p className="tabular-nums" style={{ color: C.text }}>
            {box.call.detail}
          </p>
        </div>
      ) : (
        <p className="px-2 pb-1" style={{ color: C.muted }}>
          {box.note}
        </p>
      )}
    </div>
  );
}

/**
 * On the map, top centre: the session's flag when it is not green (flags.ts mapFlag): a caution,
 * a red flag, the white flag, the checkered flag.
 */
function FlagBadge({ flag, pits }: { flag: string | null; pits: { open: boolean; short: boolean; all: boolean; cls: string } | null }) {
  const f = mapFlag(flag);
  if (!f) return null;
  const badge = (
    <div
      role="status"
      aria-label={f.label}
      className="flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wider shadow-lg"
      style={{ background: f.color, color: f.ink, border: f.checkered ? "1px solid #e5e7eb" : undefined }}
    >
      {f.checkered ? (
        <span
          aria-hidden
          className="inline-block h-3 w-4 rounded-[1px]"
          style={{ backgroundImage: "repeating-conic-gradient(#ffffff 0% 25%, #0b0b18 0% 50%)", backgroundSize: "6px 6px" }}
        />
      ) : (
        <svg viewBox="0 0 12 14" className="h-3.5 w-3" fill="currentColor" aria-hidden>
          <path d="M1 0h1.5v14H1zM3 1h8l-2 3 2 3H3z" />
        </svg>
      )}
      {f.label}
    </div>
  );
  return (
    <div data-map-overlay className="pointer-events-none absolute left-1/2 top-2 flex -translate-x-1/2 flex-wrap items-center justify-center gap-1.5">
      {badge}
      {pits && <PitLight {...pits} />}
    </div>
  );
}

/**
 * Beside the caution flag: the pit lane for the focus car's class (pitLaneState), a red light while
 * it is closed to the class, green once it opens for it ("all cars" when race control opened it for
 * every class). A short yellow keeps it closed until race control opens it for every class as the
 * field comes to the restart.
 */
function PitLight({ open, short, all, cls }: { open: boolean; short: boolean; all: boolean; cls: string }) {
  const color = open ? "#22c55e" : "#ef4444";
  const text = open ? `Pits open · ${all ? "all cars" : cls}` : short ? "Pits closed · short yellow" : `Pits closed · ${cls}`;
  const label = open ? `Pit lane open to ${all ? "all cars" : cls}` : short ? "Pit lane closed: a short yellow (it opens for every class as the field comes to the restart)" : `Pit lane not yet open to ${cls}`;
  return (
    <div
      role="status"
      aria-label={label}
      className="flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-extrabold uppercase tracking-wider shadow-lg"
      style={{ background: "#0b0b18", borderColor: color, color: "#e0e0e8" }}
    >
      <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
      {text}
    </div>
  );
}

/**
 * On the map, bottom right: turn it 45° either way, and the angle now (tap to put it back
 * upright). With the mouse the map also turns by dragging it.
 */
function TurnButtons({ angle, readout, onTurn }: { angle: number; readout: React.RefObject<HTMLSpanElement | null>; onTurn: (deg: number) => void }) {
  useEffect(() => {
    if (readout.current) readout.current.textContent = `${Math.round(angle)}°`;
  }, [angle, readout]);
  const btn = "pointer-events-auto flex h-7 min-w-7 items-center justify-center rounded-md border px-1.5 text-xs font-semibold hover:bg-white/10";
  const style = { borderColor: "#3a3a5c", color: C.text, background: OVER_BG };
  return (
    <div data-map-overlay className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1" role="group" aria-label="Turn the map">
      <button type="button" className={btn} style={style} onClick={() => onTurn(stepDeg(angle, -1))} title="Turn the map 45° anticlockwise" aria-label="Turn the map 45° anticlockwise">
        ↺
      </button>
      <button
        type="button"
        className={`${btn} tabular-nums`}
        style={{ ...style, color: angle ? C.text : C.muted }}
        onClick={() => onTurn(0)}
        title="The map's angle: drag the map with the mouse to turn it (it snaps every 45°; hold Shift to turn it freely). Tap to put it back."
        aria-label={`Map turned ${Math.round(angle)}°: put it back`}
      >
        {/* Written here and by a drag (which turns the map without re-rendering): never by React. */}
        <span ref={readout} />
      </button>
      <button type="button" className={btn} style={style} onClick={() => onTurn(stepDeg(angle, 1))} title="Turn the map 45° clockwise" aria-label="Turn the map 45° clockwise">
        ↻
      </button>
    </div>
  );
}

/** A followed car's pit ghost (its ring and label). */
const GHOST_FOLLOWED = "#e0e0e8";
/** The pit ghost's ring: in clear air, in traffic. */
const GHOST_CLEAR = "#4ade80";
const GHOST_TRAFFIC = "#fab219";

/** The cars-closing panel on the map, on or off, kept on this device. */
const CLOSING_MARKS_KEY = "rt.live.closingMarks";

/** Where the Live Crossing Order is measured from, kept on this device. */
const CROSS_FROM_KEY = "rt.live.crossingFrom";

/** The view beside the map, kept on this device. */
const ORDER_VIEW_KEY = "rt.live.orderView";


/** A safety car the timing sends as a car of its own ("SC", "PC1"…), if any. */
export function findSafetyCar(motions: Map<string, CarMotion>): CarMotion | null {
  for (const m of motions.values()) if (/^(SC|PC)\d*$/i.test(m.num.trim())) return m;
  return null;
}

/** Under the map: the cars followed (tap × to stop following one), or how to follow one. */
function FollowBar({
  data,
  focusNum,
  followed,
  out,
  onChange,
}: {
  data: RaceChartData;
  focusNum: number;
  followed: string[];
  out: Set<number>;
  onChange: (cars: string[]) => void;
}) {
  const cars = followed.filter((n) => {
    const k = carKey(data, n);
    return k !== null && !!data.cars[String(k)] && k !== focusNum;
  });
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t px-3 py-2 text-xs sm:px-6" style={{ borderColor: "#1e1e38", color: C.muted }}>
      <span className="mr-1 font-semibold" style={{ color: C.text }}>
        Following
      </span>
      {focusNum > 0 && (
        <span className="rounded-full border-2 px-2 py-0.5 font-bold tabular-nums" style={{ borderColor: TRACE.focus, color: C.text }} title="The focus car (change it with Focus)">
          #{carNo(data, focusNum)}
        </span>
      )}
      {cars.map((n) => {
        const k = carKey(data, n)!;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(followed.filter((c) => c !== n))}
            className="flex min-h-[44px] items-center gap-1 rounded-full border-2 border-white px-2.5 font-bold tabular-nums hover:bg-white/10 sm:min-h-0 sm:px-2 sm:py-0.5"
            style={{ color: out.has(k) ? C.muted : C.text, background: `${liveClassColor(data.cars[String(k)]?.cls)}33` }}
            aria-label={`Stop following #${n}`}
          >
            #{n} <span aria-hidden>×</span>
          </button>
        );
      })}
      {cars.length > 0 ? (
        <button type="button" onClick={() => onChange([])} className="ml-1 min-h-[44px] underline hover:text-white sm:min-h-0">
          Clear
        </button>
      ) : (
        <span>Tap cars on the map or in the table to follow them.</span>
      )}
    </div>
  );
}
